<template>
  <div class="case-information">
    <div v-if="Object.keys(caseDetailsSfdc).length">
      <div class="case-information__header">
        Case Information
        <Button
          label="Create ISR request"
          appearance="secondary"
          icon="pencil"
          icon-position="left"
          :disabled="!getIsEditor || !caseDetails?.isDisputeEditable"
          @onClick="createIsrClickHandler" />
      </div>
      <div class="case-information__tab-area">
        <tabs :items="caseInformationTabs" @onClick="onTabActivated" />
      </div>
      <div class="case-information__tab-content">
        <dispute-case-details
          v-if="currentSelectedTab === 'case-details'"
          :case-details-sfdc="caseDetailsSfdc" />
        <div
          v-else-if="currentSelectedTab === 'related-isr-cases'"
          :class="tableParentClass">
          <Table
            :default-columns="isrColumns"
            :row-data="isrTableData"
            primary-key="case_number"
            cell-background="#fff"
            header-cell-background="#f7f7f7"
            :header-position-top="0"
            @addParentClass="addParentClass">
            <template #default="{ row, col }">
              <span v-if="col.slotKey === 'case_number'">
                <a
                  v-if="getIsEditor"
                  href="javascript:void(0)"
                  class="case-information__tab-content__caseno"
                  @click="openIsrModel(row)">
                  {{ row.case_number }}
                </a>
                <span v-else>{{ row.case_number }}</span>
              </span>
              <span v-else-if="col.slotKey === 'last_modified_by_name'">
                {{
                  row[col.slotKey] === "Maersk API Management"
                    ? row.created_by_name
                    : row[col.slotKey]
                }}
              </span>
              <span
                v-else
                v-tooltip="getTableCellValue(row, col)"
                class="table-cell">
                {{ getTableCellValue(row, col) }}
              </span>
            </template>
          </Table>
        </div>
        <attachment-list
          v-else-if="currentSelectedTab === 'attachments'"
          :attachments="attachments"
          has-download
          :has-delete="false"
          class="case-information__tab-content__attachment-list"
          @downloadAttachment="downloadAttachment" />
        <email-activities
          v-else-if="currentSelectedTab === 'emails'"
          :email-activities="emailActivities"
          :case-data="caseDetailsSfdc"
          @downloadAttachment="downloadAttachment" />
      </div>
      <CreateISR
        v-if="activeModal === 'create_isr'"
        :case-details-sfdc="caseDetailsSfdc"
        @onModalClose="onModalClose"
        @refreshIsrData="refreshIsrData"
        v-on="$listeners" />
      <ISRDetails
        v-if="activeModal === 'isr_details' && isrDetails"
        :case-details-sfdc="caseDetailsSfdc"
        :isr-details="isrDetails"
        @onModalClose="onModalClose"
        @refreshIsrData="refreshIsrData"
        v-on="$listeners"
        @downloadAttachment="downloadAttachment" />
    </div>
    <div v-else>
      Case Information in not available in Sfdc for this invoice number
    </div>
  </div>
</template>

<script>
// Components
import DisputeCaseDetails from "@/components/DisputeCaseInformation/DisputeCaseDetails";
import EmailActivities from "@/components/EmailActivities/EmailActivities.vue";
import CreateISR from "@/components/ISR/CreateISR";
import ISRDetails from "@/components/ISR/ISRDetails";

// Utils
import { saveAs } from "file-saver";
import AxiosFetchForMsal from "@/utils/axiosForMsal";
import { getMsalIdToken } from "@/utils/helpers";
import { format } from "date-fns";
import { mapGetters } from "vuex";

// constants
import {
  caseInformationTabs,
  related_isr_table_config,
} from "@/config/caseInformationConstant.js";

//Actions
import { fileActions } from "@/actions";

export default {
  name: "DisputeCaseInformation",
  components: {
    DisputeCaseDetails,
    CreateISR,
    ISRDetails,
    EmailActivities,
  },
  props: {
    caseDetailsSfdc: {
      type: Object,
      required: true,
    },
    caseDetails: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      caseInformationTabs,
      currentSelectedTab: "case-details",
      isrTableData: [],
      isrColumns: related_isr_table_config,
      tableParentClass: "",
      activeModal: null,
      isrDetails: null,
      attachments: [],
      emailActivities: [],
    };
  },
  computed: {
    ...mapGetters(["getIsEditor"]),
  },
  methods: {
    addParentClass(val) {
      this.tableParentClass = val;
    },
    onTabActivated({ selectedItemId, updatedTabData }) {
      const tabDetails = {
        "related-isr-cases": () => {
          this.$emit(
            "fetchRelatedIsrFromSfdc",
            this.isrTableData.length,
            (data) =>
              (this.isrTableData = data.sort(
                (a, b) => new Date(b.created_date) - new Date(a.created_date)
              ))
          );
          return;
        },
        attachments: () => {
          this.$emit(
            "fetchAttachmentsFromSfdc",
            this.attachments.length,
            this.caseDetailsSfdc?.id,
            (data) => {
              this.attachments = (data || []).map((item) => ({
                fileName: item?.name,
                type: item?.file_extension || item?.name.split(".").pop(),
                id: item?.id,
                createdBy: item?.created_by_name,
                lastModifiedDate: item?.last_modified_date,
              }));
            }
          );
          return;
        },
        emails: () => {
          this.$emit(
            "fetchEmailActivities",
            this.emailActivities.length,
            this.caseDetailsSfdc?.id,
            (data) => {
              this.emailActivities = data;
            }
          );
          return;
        },
        default: () => {
          return;
        },
      };
      this.currentSelectedTab = selectedItemId;
      this.caseInformationTabs = updatedTabData;
      (tabDetails[`${selectedItemId}`] || tabDetails["default"])();
    },
    createIsrClickHandler() {
      this.activeModal = "create_isr";
    },
    onModalClose() {
      this.activeModal = null;
      this.isrDetails = null;
    },
    openIsrModel(row) {
      this.attachments = [];
      this.emailActivities = [];
      this.activeModal = "isr_details";
      this.$emit("fetchISRDetails", row.id, (data) => {
        const countryObj = {
          id: 1,
          name: data?.country_name,
          label: data?.country_name,
          value: data?.country_name,
        };
        const cityObj = {
          id: 1,
          name: data?.city_name,
          label: data?.city_name,
          value: data?.city_name,
        };
        this.isrDetails = {
          ...data,
          country: countryObj,
          city: cityObj,
          countryList: [countryObj],
          cityList: [cityObj],
        };
      });
    },
    async downloadAttachment(file) {
      try {
        const { fileName, id } = file;
        const { data } = await AxiosFetchForMsal(
          fileActions.downloadAttachmentSfdc(
            id,
            `Bearer ${await getMsalIdToken(this)}`
          )
        );
        const fileItem = new Blob([data], {
          type: `application/${file?.type || "pdf"}`,
        });
        saveAs(fileItem, `${fileName}.${file?.type || "pdf"}`);
      } catch (error) {
        console.error(error);
      }
    },
    getTableCellValue(row, col) {
      const value = row[col.slotKey];
      if (col.type === "date") {
        try {
          return value ? format(new Date(value), "dd-MMM-yyyy hh:mm a") : "N/A";
        } catch (err) {
          return value;
        }
      }
      return !value || typeof value === "undefined" || value === "null"
        ? "-"
        : value;
    },
    getChargeCodeValue(value) {
      let chargeCodeValue;
      value.split("+").forEach((value) => {
        chargeCodeValue = chargeCodeValue
          ? chargeCodeValue + "+" + value?.split("-")?.[0]
          : value?.split("-")?.[0];
      });
      return chargeCodeValue;
    },
    refreshIsrData() {
      this.$emit(
        "fetchRelatedIsrFromSfdc",
        this.isrTableData.length,
        (data) =>
          (this.isrTableData = data.sort(
            (a, b) => new Date(b.created_date) - new Date(a.created_date)
          ))
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.case-information {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__tab-content {
    &__caseno {
      color: var(--Blue-600-Dark);
    }
    &__attachment-list {
      margin-top: 1rem;
    }
  }
}
</style>

<style lang="scss">
.case-information {
  .table-parent-style {
    overflow: auto;
  }
}
</style>
